import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import DataSource from "devextreme/data/data_source";
import { finalize, map, of, switchMap, tap } from "rxjs";
import { KeyValuePair, InstanceRequest } from "../../core";
import { DemoTemplateViewModel } from "../../core/model/ContentManagement";
import { IntendedUseEnum, boardIntendedUses, partnerIntendedUses } from "../../core/model/IntendedUses";
import { DemoInstancesService } from "../../services/demo-instances.service";
import { DemoTemplateService } from "../../services/demo-template.service";
import { MetadataService } from "../../services/metadata.service";
import { OpportunityService } from "../../services/opportunity.service";
import { RegionService } from "../../services/region.service";
import { UserService } from "../../services/user.service";
import { brdCatchError } from "../../shared/message-utils";

export interface CreateInstancePopupData {
  selectedDemos: {
    demoId: number;
    name: string;
    isDefault: boolean;
  }[];
  selectedTemplate: DemoTemplateViewModel;
}

@Component({
  selector: "brd-create-instance-popup",
  templateUrl: "./create-instance-popup.component.html",
  styleUrl: "./create-instance-popup.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CreateInstancePopupComponent implements OnInit {
  selectedIndex = 0;
  uses: KeyValuePair<number>[];
  opportunities?: DataSource;
  noDataText = "Please type at least 3 digits";
  opportunitySearchText = "";
  regionDataSource$ = this.regionService.regions$;

  passwordFormControl = new FormControl(InstanceRequest.generateNewPassword(), [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(12),
    Validators.pattern(InstanceRequest.passwordPattern)
  ]);
  intendedUseFormControl = new FormControl<number | null>(null, Validators.required);
  titleFormControl = new FormControl("", Validators.required);
  regionControl = new FormControl<KeyValuePair<string> | null>(null, Validators.required);

  opportunityControl = new FormControl<KeyValuePair<string> | string | null>(null, Validators.required);

  formGroup = new FormGroup({
    title: this.titleFormControl,
    note: new FormControl(""),
    intendedUse: this.intendedUseFormControl,
    opportunity: this.opportunityControl,
    region: this.regionControl,
    password: this.passwordFormControl
  });

  showOpportunities = false;
  showComment = false;
  newPasswordButton = {
    icon: "refresh",
    location: "after",
    onClick: (): void => {
      this.passwordFormControl.setValue(InstanceRequest.generateNewPassword());
    }
  };

  recapValues: {
    title?: string;
    note?: string;
    intendedUse?: string;
    opportunity?: string;
    region?: string;
  } = {};

  loading = false;
  create = false;

  destroyRef = inject(DestroyRef);

  latestVersion$ = this.metadataService.latestVersion$;

  public constructor(
    private dialogRef: MatDialogRef<CreateInstancePopupData>,
    @Inject(MAT_DIALOG_DATA) public data: CreateInstancePopupData,
    private opportunityService: OpportunityService,
    private regionService: RegionService,
    private templateService: DemoTemplateService,
    private instanceService: DemoInstancesService,
    private readonly dialog: MatDialog,
    private metadataService: MetadataService,
    private userService: UserService
  ) {
    this.intendedUseFormControl.valueChanges
      .pipe(
        takeUntilDestroyed<number | null>(this.destroyRef),
        tap(res => {
          this.showOpportunities = res === IntendedUseEnum.opportunity;
          this.showComment = !this.showOpportunities;

          this.opportunityControl.setValue(null);
        })
      )
      .subscribe();

    this.uses = this.userService.isBoardUser() ? [...boardIntendedUses] : [...partnerIntendedUses];
  }

  ngOnInit(): void {
    this.opportunities = new DataSource({
      store: this.opportunityService.getOpportunitiesStore(),
      pageSize: 10,
      paginate: true
    });
  }

  onNoClick(): void {
    if (!this.loading) this.dialogRef.close();
  }

  nextClick(): void {
    if (this.formGroup.valid) {
      this.selectedIndex = 1;
      const { title, intendedUse, opportunity, region } = this.formGroup.value;
      const use = this.uses.find(x => x.id === intendedUse);

      this.recapValues = {
        title: title ?? "",
        intendedUse: use?.name,
        opportunity:
          opportunity && intendedUse === IntendedUseEnum.opportunity ? (opportunity as KeyValuePair<string>).name : (opportunity as string),
        region: region?.name
      };
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  prevClick(): void {
    this.selectedIndex = 0;
  }

  opportunityTypeSearch(): void {
    if (this.opportunitySearchText.length >= 3) {
      this.noDataText = "No data to display";
    } else {
      this.noDataText = "Please type at least 3 digits";
    }
  }

  createInstance(): void {
    if (this.formGroup.valid) {
      const { title, intendedUse, opportunity, region, password } = this.formGroup.value;
      this.loading = true;
      this.create = false;

      const templateObs = this.data.selectedTemplate
        ? of(this.data.selectedTemplate.id)
        : this.templateService
            .addOrUpdateTemplate({
              name: `${title}-template`,
              isEnabled: false,
              id: 0,
              demoAssets: this.data.selectedDemos.filter(x => !x.isDefault).map(x => x.demoId),
              description: "",
              estimatedTime: 0,
              version: "1.0",
              vmImageId: 0
            })
            .pipe(map(res => res.id));

      templateObs
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(templateId =>
            this.instanceService.addInstance({
              title: title!,
              password: password!,
              intendedUse: intendedUse as IntendedUseEnum,
              prospect:
                intendedUse === IntendedUseEnum.opportunity && opportunity
                  ? (opportunity as KeyValuePair<string>).name
                  : (opportunity as string),
              prospectId: intendedUse === IntendedUseEnum.opportunity && opportunity ? (opportunity as KeyValuePair<string>).id : undefined,
              region: region!,
              id: "",
              hdpUsername: "",
              link: "",
              demoTemplateId: templateId,
              comment: ""
            })
          ),
          brdCatchError(),
          finalize(() => {
            if (!this.create) this.dialogRef.close();
          })
        )
        .subscribe(res => {
          this.create = !!res;
          this.dialogRef.close(res);
        });
    }
  }
}
